const EPSILON = 1e-10;
const MAX_PRECISION = 6;

export const digitsAfterDot = (num: number) => {
	if (Number.isNaN(Number(num))) return 0;

	let scaled = num;
	let count = 0;
	while (scaled % 1 !== 0 && count < MAX_PRECISION) {
		scaled *= 10;
		count++;
	}
	return count;
};

const limit = (number: number, precision: number) => {
	if (Number.isNaN(Number(number))) return 0;
	const factor = 10 ** Math.min(precision, MAX_PRECISION);
	return Math.round(number * factor) / factor;
};

const findOrderIndex = (orders: number[], target: number): number => {
	return orders.findIndex((o) => Math.abs(o - target) < EPSILON);
};

const findSimplestNumber = (start: number, end: number): number => {
	const diff = end - start;
	const middle = limit(start + diff / 2, MAX_PRECISION);

	const maxDigits = Math.max(digitsAfterDot(start), digitsAfterDot(end));
	const step = 10 ** -maxDigits;

	const result = limit(Math.round(middle / step) * step, maxDigits);

	if (result <= start || result >= end) return middle;
	return result;
};

const orderBefore = (first: number) => (first > 0 ? findSimplestNumber(0, first) : 0.5);

const rawOrderAfter = (orders: number[], order: number) => {
	if (!Array.isArray(orders) || orders.length === 0) return 1;
	if (Number.isNaN(Number(order)) || order < 0) return orderBefore(orders[0]);

	const currentIndex = findOrderIndex(orders, order);

	if (currentIndex === -1) {
		const insertIndex = orders.findIndex((o) => o > order);

		if (insertIndex === -1) return limit(orders[orders.length - 1] + 1, 1);
		if (insertIndex === 0) return orderBefore(orders[0]);

		const prev = orders[insertIndex - 1];
		const next = orders[insertIndex];

		return findSimplestNumber(prev, next);
	}

	if (currentIndex === orders.length - 1) return limit(order + 1, 1);

	const current = orders[currentIndex];
	const next = orders[currentIndex + 1];

	return findSimplestNumber(current, next);
};

const rawOrderTryRound = (orders: number[], order: number) => {
	if (Number.isNaN(Number(order))) return 1;

	const digits = Math.min(digitsAfterDot(order), MAX_PRECISION);

	if (digits <= 1) return order;

	const diff = 10 ** (-1 * digits);
	const ceil = limit(order + diff, digits);

	if (!orders.some((o) => Math.abs(o - ceil) < EPSILON)) {
		return limit(ceil, digits);
	}

	return limit(order, digits);
};

export const roundedOrderAfter = (orders: number[], order: number) => {
	if (!Array.isArray(orders)) return 1;
	const validOrders = orders.filter((o) => Number.isFinite(Number(o)));
	const rawOrder = rawOrderAfter(validOrders, order);
	return rawOrderTryRound(validOrders, rawOrder);
};
